import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { tenantsApi } from "../api/client.ts";
import { useAuth } from "../lib/auth.tsx";
import { PasswordField } from "../components/PasswordField.tsx";

export function SettingsPage() {
  const { tenant, refresh } = useAuth();
  if (!tenant) return null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Settings</h2>
        <p className="text-sm text-slate-500">
          Account, billing model, and mobile money credentials.
        </p>
      </div>
      <AccountSection refreshAuth={refresh} />
      <BillingSection />
      <DarajaSection />
    </div>
  );
}

// =========================================================================
// Account section — name + phone
// =========================================================================

const AccountSchema = z.object({
  name: z.string().min(1).max(120),
  phone: z
    .string()
    .min(7)
    .max(20)
    .optional()
    .or(z.literal("").transform(() => undefined)),
});
type AccountValues = z.infer<typeof AccountSchema>;

function AccountSection({ refreshAuth }: { refreshAuth: () => Promise<void> }) {
  const { tenant } = useAuth();
  const [status, setStatus] = useState<"idle" | "saved" | "error">("idle");
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AccountValues>({
    resolver: zodResolver(AccountSchema),
    defaultValues: { name: tenant?.name ?? "", phone: "" },
  });

  const onSubmit = handleSubmit(async (values) => {
    setStatus("idle");
    setServerError(null);
    try {
      await tenantsApi.updateTenant(tenant!.idToken, {
        name: values.name,
        phone: values.phone ?? null,
      });
      setStatus("saved");
      // Pull a fresh ID token so `tenant.name` in the UI updates without reload.
      await refreshAuth();
    } catch (err) {
      setStatus("error");
      setServerError(err instanceof Error ? err.message : "Save failed");
    }
  });

  return (
    <Section title="Account">
      <form onSubmit={onSubmit} className="grid gap-4 sm:grid-cols-2">
        <Field label="Business name" error={errors.name?.message}>
          <input
            type="text"
            {...register("name")}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </Field>
        <Field label="Contact phone" error={errors.phone?.message}>
          <input
            type="tel"
            placeholder="0712 345 678"
            {...register("phone")}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </Field>
        <div className="sm:col-span-2">
          <p className="text-xs text-slate-500">
            Email: <span className="font-mono">{tenant?.email}</span> (managed via your Cognito login)
          </p>
        </div>
        <div className="flex items-center gap-3 sm:col-span-2">
          <button
            type="submit"
            disabled={isSubmitting}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {isSubmitting ? "Saving…" : "Save account"}
          </button>
          {status === "saved" && (
            <span className="inline-flex items-center gap-1 text-xs text-brand-700">
              <CheckCircle2 className="h-3.5 w-3.5" /> Saved
            </span>
          )}
          {status === "error" && serverError && (
            <span className="text-xs text-red-700">{serverError}</span>
          )}
        </div>
      </form>
    </Section>
  );
}

// =========================================================================
// Billing model section
// =========================================================================

function BillingSection() {
  const { tenant } = useAuth();
  const qc = useQueryClient();
  const [status, setStatus] = useState<"idle" | "saved" | "error">("idle");
  const [serverError, setServerError] = useState<string | null>(null);
  const [model, setModel] = useState<"flat" | "revenue_share">("flat");

  const save = useMutation({
    mutationFn: () =>
      tenantsApi.updateTenant(tenant!.idToken, { billingModel: model }),
    onSuccess: () => {
      setStatus("saved");
      qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (err) => {
      setStatus("error");
      setServerError(err instanceof Error ? err.message : "Save failed");
    },
  });

  return (
    <Section title="Billing model">
      <p className="text-sm text-slate-600">
        Pick how SpotMesh bills you. Switch any time; the new model takes
        effect at the start of your next billing cycle.
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <Choice
          checked={model === "flat"}
          onClick={() => setModel("flat")}
          title="Flat per-router"
          subtitle="KES 499 / month per router. Predictable bill."
        />
        <Choice
          checked={model === "revenue_share"}
          onClick={() => setModel("revenue_share")}
          title="3% revenue share"
          subtitle="KES 500 / month minimum. We earn when you earn."
        />
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {save.isPending ? "Saving…" : "Save billing model"}
        </button>
        {status === "saved" && (
          <span className="inline-flex items-center gap-1 text-xs text-brand-700">
            <CheckCircle2 className="h-3.5 w-3.5" /> Saved
          </span>
        )}
        {status === "error" && serverError && (
          <span className="text-xs text-red-700">{serverError}</span>
        )}
      </div>
    </Section>
  );
}

function Choice({
  checked,
  onClick,
  title,
  subtitle,
}: {
  checked: boolean;
  onClick: () => void;
  title: string;
  subtitle: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-start rounded-md border p-4 text-left transition ${
        checked
          ? "border-brand-500 bg-brand-50 ring-1 ring-brand-500"
          : "border-slate-200 hover:border-slate-300"
      }`}
    >
      <p className="text-sm font-medium text-slate-900">{title}</p>
      <p className="mt-1 text-xs text-slate-600">{subtitle}</p>
    </button>
  );
}

// =========================================================================
// Daraja credentials section
// =========================================================================

const DarajaSchema = z.object({
  environment: z.enum(["sandbox", "production"]),
  shortcode: z.string().regex(/^\d{5,7}$/, "5–7 digits"),
  consumerKey: z.string().min(1).max(200),
  consumerSecret: z.string().min(1).max(200),
  passkey: z.string().min(1).max(500),
  transactionType: z.enum([
    "CustomerPayBillOnline",
    "CustomerBuyGoodsOnline",
  ]),
});
type DarajaValues = z.infer<typeof DarajaSchema>;

function DarajaSection() {
  const { tenant } = useAuth();
  const [status, setStatus] = useState<"idle" | "saved" | "error">("idle");
  const [serverError, setServerError] = useState<string | null>(null);

  const configured = useQuery({
    queryKey: ["daraja-status", tenant!.tenantId],
    queryFn: () => tenantsApi.getDarajaStatus(tenant!.idToken),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<DarajaValues>({
    resolver: zodResolver(DarajaSchema),
    defaultValues: {
      environment: "sandbox",
      shortcode: "",
      consumerKey: "",
      consumerSecret: "",
      passkey: "",
      transactionType: "CustomerPayBillOnline",
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    setStatus("idle");
    setServerError(null);
    try {
      await tenantsApi.putDaraja(tenant!.idToken, values);
      setStatus("saved");
      configured.refetch();
      // Clear the secret values from the form so they don't sit in the DOM.
      reset({
        environment: values.environment,
        shortcode: values.shortcode,
        consumerKey: "",
        consumerSecret: "",
        passkey: "",
        transactionType: values.transactionType,
      });
    } catch (err) {
      setStatus("error");
      setServerError(err instanceof Error ? err.message : "Save failed");
    }
  });

  return (
    <Section title="Mobile money (Daraja / M-Pesa)">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-slate-600">
          Your Daraja credentials are stored encrypted in AWS Secrets Manager.
          We never expose them back to the browser — saving here overwrites
          the stored values.
        </p>
        <ConfigBadge state={configured.data?.configured} />
      </div>
      <form onSubmit={onSubmit} className="mt-4 grid gap-4 sm:grid-cols-2">
        <Field label="Environment" error={errors.environment?.message}>
          <select
            {...register("environment")}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="sandbox">Sandbox</option>
            <option value="production">Production</option>
          </select>
        </Field>
        <Field label="Transaction type" error={errors.transactionType?.message}>
          <select
            {...register("transactionType")}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="CustomerPayBillOnline">Paybill</option>
            <option value="CustomerBuyGoodsOnline">Buy Goods (Till)</option>
          </select>
        </Field>
        <Field label="Shortcode" error={errors.shortcode?.message}>
          <input
            type="text"
            inputMode="numeric"
            placeholder="174379"
            {...register("shortcode")}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Consumer key" error={errors.consumerKey?.message}>
          <PasswordField invalid={!!errors.consumerKey} {...register("consumerKey")} />
        </Field>
        <Field label="Consumer secret" error={errors.consumerSecret?.message}>
          <PasswordField invalid={!!errors.consumerSecret} {...register("consumerSecret")} />
        </Field>
        <Field label="Passkey" error={errors.passkey?.message}>
          <PasswordField invalid={!!errors.passkey} {...register("passkey")} />
        </Field>
        <div className="flex items-center gap-3 sm:col-span-2">
          <button
            type="submit"
            disabled={isSubmitting}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {isSubmitting ? "Saving…" : "Save Daraja credentials"}
          </button>
          {status === "saved" && (
            <span className="inline-flex items-center gap-1 text-xs text-brand-700">
              <CheckCircle2 className="h-3.5 w-3.5" /> Saved to Secrets Manager
            </span>
          )}
          {status === "error" && serverError && (
            <span className="text-xs text-red-700">{serverError}</span>
          )}
        </div>
      </form>
    </Section>
  );
}

function ConfigBadge({ state }: { state: boolean | undefined }) {
  if (state === undefined)
    return (
      <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-500">
        checking…
      </span>
    );
  if (state)
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-brand-200 bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
        <CheckCircle2 className="h-3 w-3" /> configured
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
      <AlertCircle className="h-3 w-3" /> not yet configured
    </span>
  );
}

// =========================================================================
// Shared bits
// =========================================================================

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </h3>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-700">{label}</span>
      <div className="mt-1">{children}</div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </label>
  );
}
